import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Buton, Iskelet, Kart, MetinAlani, NotKutusu, Secim } from "@koclukpanel/ui";
import { ApiHatasi, benGetir, etkinlikDokumu, type Ben, type KisiDokumu } from "../api";
import { izinKapsami } from "../izin";
import { modulAdi } from "../modul-adi";

/**
 * İŞLEM GEÇMİŞİ — Personel 360°, G-IZL-03a
 *
 * ================================================================
 * YENİ BİR ERİŞİM YOLU AÇILMADI
 * ================================================================
 * Bu sekme mevcut `POST /denetim/etkinlik/kisi` ucunu çağırıyor. O uç
 * zaten gerekçe istiyor, `ALL` kapsamı arıyor ve açılışın kendisini
 * **senkron** bir güvenlik olayı olarak yazıyor.
 *
 * 360 ekranı için ayrı bir "personelin işlemleri" ucu yazsaydık, aynı
 * veriye gerekçesiz ikinci bir kapı açılmış olurdu — ve bu depoda 360
 * ekranlarının asıl riski tam olarak bu: veri bugüne kadar ayrı
 * ekranlara dağıldığı için kazayla korunuyordu.
 *
 * ================================================================
 * KENDİ DÖKÜMÜ GEREKÇESİZ
 * ================================================================
 * Kişi kendi kaydına bakıyorsa gerekçe sorulmuyor; gerekçe istemek
 * kendi verisine bakmayı bir yetki sınavına çevirirdi. Başkasınınki
 * `ALL` kapsamı + yazılı gerekçe istiyor. Sekme `denetim.etkinlik.kisi`
 * iznine bağlı; 14.09.2026'dan beri o izin yalnızca SUPER_ADMIN ve
 * TECH_ADMIN'de (matris §5.17).
 */

const ARALIKLAR = [7, 30, 90] as const;

export function IslemGecmisi({
  personelId,
  personelAdi,
}: {
  personelId: string;
  personelAdi: string;
}) {
  const [gun, setGun] = useState<number>(30);
  const [gerekce, setGerekce] = useState("");
  const [istenen, setIstenen] = useState<{ gun: number; gerekce: string } | null>(null);

  /*
    Aynı sorgu anahtarı kabukta zaten kullanılıyor; yanıt önbellekte ve
    bu sorgu ek bir istek üretmiyor.
  */
  const ben = useQuery<Ben>({ queryKey: ["ben"], queryFn: benGetir, retry: false });

  const kendisiMi = ben.data?.userId === personelId;

  /*
    KAPSAM soruluyor, VARLIK değil: uç başkasının dökümü için `ALL`
    istiyor. Dar kapsamlı biri düğmeyi görseydi her tıklamada 403
    alırdı — ekran kendi kuralını kurmuyor, sunucunun kullandığı aynı
    haritayı okuyor.
  */
  const baskasiniGorebilir =
    ben.data !== undefined && izinKapsami(ben.data.izinler, "denetim.etkinlik.kisi") === "ALL";

  const dokum = useQuery<KisiDokumu>({
    queryKey: ["personel-dokum", personelId, istenen?.gun, istenen?.gerekce],
    queryFn: () => etkinlikDokumu(personelId, istenen!.gun, istenen!.gerekce),
    enabled: istenen !== null,
    retry: false,
  });

  // Sunucudaki eşikle aynı sayı; sunucu yine de kontrol ediyor.
  const gerekceKisa = !kendisiMi && gerekce.trim().length < 15;

  if (ben.isPending) return <Iskelet satir={3} />;

  if (!kendisiMi && !baskasiniGorebilir) {
    /*
      YETKİSİ OLMAYANA ŞARTI ANLATMIYORUZ. "Gerekçe yazarsanız
      açabilirsiniz" demek, gerekçe yazınca açılacağını söylemek olur —
      açılmaz. Sebep söyleniyor, umut verilmiyor.
    */
    return (
      <Kart baslik="İşlem geçmişi">
        <p className="text-notr-700 text-sm">
          Başka bir kullanıcının işlem dökümünü açma yetkiniz yok. Bu döküm yalnızca tüm kapsamda
          denetim yetkisi olan kullanıcılara açılır.
        </p>
      </Kart>
    );
  }

  return (
    <Kart
      baslik="İşlem geçmişi"
      aciklama={`${personelAdi} adlı kişinin denetim kaydına düşmüş işlemlerinin modül bazlı özeti.`}
    >
      <div className="flex flex-col gap-3">
        {!kendisiMi && (
          <NotKutusu ton="uyari">
            <strong>Bu döküm gizli açılmaz.</strong> Gerekçeniz denetim kaydına yazılır ve
            silinemez; açılışın kendisi bir güvenlik olayı olarak kaydedilir.
          </NotKutusu>
        )}

        <Secim
          etiket="Aralık"
          value={String(gun)}
          onChange={(e) => {
            setGun(Number(e.target.value));
            // Aralık değişince eski döküm ekranda KALMIYOR: başka bir
            // aralığın sayılarını yeni başlıkla göstermek yanlış olurdu.
            setIstenen(null);
          }}
          className="w-44"
        >
          {ARALIKLAR.map((a) => (
            <option key={a} value={a}>
              Son {a} gün
            </option>
          ))}
        </Secim>

        {!kendisiMi && (
          <MetinAlani
            etiket="Gerekçe"
            rows={2}
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            yardim="En az 15 karakter. Altı ay sonra “bu dökümü neden açtım” sorusunu cevaplamalı."
          />
        )}

        <div>
          <Buton
            disabled={gerekceKisa}
            onClick={() => setIstenen({ gun, gerekce: kendisiMi ? "" : gerekce.trim() })}
          >
            Dökümü getir
          </Buton>
        </div>

        {dokum.isFetching && <Iskelet satir={3} />}

        {dokum.isError && (
          <p role="alert" className="text-hata-700 text-sm">
            {dokum.error instanceof ApiHatasi ? dokum.error.message : "Döküm alınamadı."}
          </p>
        )}

        {dokum.data && !dokum.isFetching && (
          <div role="status" className="flex flex-col gap-3">
            <p className="text-notr-700 text-sm">
              {new Date(dokum.data.baslangic).toLocaleDateString("tr-TR")} –{" "}
              {new Date(dokum.data.bitis).toLocaleDateString("tr-TR")} arasında{" "}
              <strong>{dokum.data.toplamIslem}</strong> işlem.
            </p>

            {dokum.data.moduller.length === 0 ? (
              /*
                "Kayıt yok" ile "işlem yapmadı" AYNI ŞEY DEĞİL: denetim
                kaydına düşmeyen işlemler var (okumaların çoğu) ve
                `worker` çalışmadıysa kuyruktakiler henüz yazılmamış
                olabilir. Metin bunu söylüyor.
              */
              <p className="text-notr-600 text-sm">
                Bu aralıkta denetim kaydına düşmüş işlem yok. Bu, hiç çalışmadığı anlamına gelmez —
                her işlem kayda girmiyor.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {dokum.data.moduller.map((m) => (
                  <li
                    key={m.anahtar}
                    className="border-notr-200 flex justify-between border-b py-1 text-sm last:border-b-0"
                  >
                    <span className="text-notr-700">{modulAdi(m.anahtar)}</span>
                    <span className="text-notr-900 font-medium tabular-nums">{m.islemSayisi}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Kart>
  );
}
